import { effectiveQuoteStatus, localISODate } from './quoteStatus'
import type { JobQuoteSummary, QuoteFilter } from '../types'

/**
 * Dashboard quote-status filter (spec 008, FR-021a / research R9).
 *
 * The jobs list embeds the job's current quote through the
 * `jobs.current_quote_id` pointer (trigger-owned, always the latest version).
 * Filters that narrow on the embedded row use `!inner` so PostgREST drops
 * jobs whose quote fails the predicate; `none` filters the pointer itself and
 * keeps the plain (left) embed. "Expired" is derived from `expiry_date`, never
 * stored, so the predicates carry today's date exactly as
 * `quote_effective_status()` does in SQL.
 */

export const CURRENT_QUOTE_COLUMNS = 'id,status,quote_number,expiry_date,total_cents,version'

export const CURRENT_QUOTE_EMBED = `current_quote:quotes!jobs_current_quote_id_fkey(${CURRENT_QUOTE_COLUMNS})`

const CURRENT_QUOTE_INNER_EMBED = `current_quote:quotes!jobs_current_quote_id_fkey!inner(${CURRENT_QUOTE_COLUMNS})`

export const QUOTE_FILTER_OPTIONS: { value: QuoteFilter | 'all'; label: string }[] = [
  { value: 'all', label: 'Any quote status' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined_or_expired', label: 'Declined or expired' },
  { value: 'none', label: 'No quote' },
]

const narrowsOnEmbed = (filter?: QuoteFilter | null): boolean => !!filter && filter !== 'none'

/** The embed clause for the jobs select — `!inner` only when a filter needs it. */
export function currentQuoteEmbed(filter?: QuoteFilter | null): string {
  return narrowsOnEmbed(filter) ? CURRENT_QUOTE_INNER_EMBED : CURRENT_QUOTE_EMBED
}

/** The subset of the PostgREST filter builder the predicates use. */
interface QuoteFilterBuilder {
  in(column: string, values: readonly string[]): this
  gte(column: string, value: string): this
  eq(column: string, value: string): this
  or(filters: string, options?: { referencedTable?: string }): this
  is(column: string, value: null): this
}

export function applyQuoteFilter<Q extends QuoteFilterBuilder>(
  query: Q,
  filter: QuoteFilter,
  today: string = localISODate()
): Q {
  switch (filter) {
    case 'quoted':
      return query
        .in('current_quote.status', ['sent', 'viewed'])
        .gte('current_quote.expiry_date', today)
    case 'accepted':
      return query.eq('current_quote.status', 'accepted')
    case 'declined_or_expired':
      return query.or(`status.eq.declined,and(status.in.(sent,viewed),expiry_date.lt.${today})`, {
        referencedTable: 'current_quote',
      })
    case 'none':
      return query.is('current_quote_id', null)
  }
}

/** The same predicate over a cached row — the offline mirror of applyQuoteFilter. */
export function matchesQuoteFilter(
  quote: JobQuoteSummary | null | undefined,
  filter: QuoteFilter,
  today: string = localISODate()
): boolean {
  if (filter === 'none') return !quote
  if (!quote) return false
  const status = effectiveQuoteStatus(quote, today)
  switch (filter) {
    case 'quoted':
      return status === 'sent' || status === 'viewed'
    case 'accepted':
      return status === 'accepted'
    case 'declined_or_expired':
      return status === 'declined' || status === 'expired'
  }
}
